import React, { useState } from "react";
import { makeStyles } from "@mui/styles";
import {
  Modal,
  Backdrop,
  Fade,
  Typography,
  Button as MuiButton,
} from "@mui/material";
import { Divider, Stack, Box } from "@mui/material";
import Button from "../Button";
import Warning from "@mui/icons-material/Warning";
import MuiTooltip from "../MuiTooltip";
import Input from "../Input";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    width: "40%",
    maxHeight: "80%",
    overflowY: "auto",
    borderRadius: 10,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
      width: "auto",
    },
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    fontSize: "16px",
    lineHeight: "20px",
    marginBottom: "20px",
    borderRadius: "4px",
    border: "1px solid #E1E1E1", // Default border
    "&:focus": {
      outline: "none",
      borderColor: "#ccc", // Optional: change border color on focus
    },
  },
  visibilityToggle: {
    cursor: "pointer",
    position: "absolute",
    right: "10px",
    top: "10px",
  },
  errorText: {
    color: "red",
    fontSize: "14px",
    marginTop: "8px",
  },
  noUppercase: {
    textTransform: "none",
    color: "#565556",
    fontFamily: "DM Sans",
    fontSize: "16px",
  },
}));

const AccessKeyEnterModal = ({
  isOpen,
  handleClose,
  onSubmit = () => {},
  loading = false,
  error = "",
  setError = () => {},
  isShowCancel = false,
  description = "To unlock this Wheel, please enter the password.",
  // ISP-9999 (fix-stg0918e-email-lock-shows-enter-password-dead-end): which
  // lock is ACTUALLY blocking this visitor, off the BE's `lock_kind` denial
  // field ("access_code" | "data_entry" | "email"). Default "access_code"
  // preserves today's only prompt for every existing caller that doesn't
  // pass this. An "email" (permission_lock) lock has NO password to submit
  // at all — showing the password field + "Enter Password" copy in that
  // case was a dead end (ViewWheel/index.jsx already reads/passes lockKind;
  // this component just never consulted it).
  lockKind = "access_code",
}) => {
  const classes = useStyles();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const isEmailGrantLock = lockKind === "email";

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    error && setError("");
  };

  const onButtonPress = () => {
    onSubmit(password);
    setPassword("");
    setError("");
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      className={classes.modal}
      open={isOpen}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={isOpen}>
        <div className={classes.paper}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center">
              <Warning
                style={{ color: "rgba(245, 204, 59, 1)", marginRight: "8px" }}
              />
              <Typography
                variant="h6"
                id="transition-modal-title"
                style={{
                  color: "#565556",
                  fontFamily: "DM Sans",
                  fontWeight: 700,
                  fontSize: "20px",
                  lineHeight: "20px",
                  marginRight: "5px",
                }}
              >
                {/* ISP-9999: an "email" (permission_lock) lock has no
                    password to enter at all — "Enter Password" here was a
                    dead-end title for a visitor who was never going to have
                    one. */}
                {isEmailGrantLock ? "Restricted Access" : "Enter Password"}
              </Typography>

              <MuiTooltip
                message={
                  isEmailGrantLock
                    ? "Ask the wheel owner to grant your email access"
                    : "Request Password from Wheel Owner"
                }
                size="medium"
                sx={{ marginTop: 0.5 }}
              />
            </Box>
          </Stack>
          <Divider sx={{ mt: 2 }} variant="middle" />
          <Stack direction={"row"} alignItems={"center"} gap={2} my={2}>
            <Typography
              style={{
                fontFamily: "DM Sans",
                fontSize: 16,
                fontWeight: "500",

                color: "#565556",
              }}
            >
              {isEmailGrantLock
                ? "This Wheel is only available to people the owner has granted access to. Ask the owner to add your email to the list, or open the access link they send you — there is no password to enter."
                : description}
            </Typography>
          </Stack>
          {error && (
            <Typography
              style={{
                fontFamily: "DM Sans",
                fontSize: 12,
                fontWeight: "500",
                color: "red",
                marginBottom: 10,
              }}
            >
              {error}
            </Typography>
          )}
          {/* ISP-9999: no password input (and no Confirm submission) for an
              email-grant-only lock — there is nothing valid a visitor could
              type here, so offering the field was the dead-end trap: any
              guess just bounced with no informative error. */}
          {!isEmailGrantLock && (
            <>
              <Typography
                style={{
                  fontFamily: "DM Sans",
                  fontSize: 12,
                  fontWeight: "500",
                  color: "#123051",
                  marginBottom: 10,
                }}
              >
                Enter Password
              </Typography>
              <div style={{ position: "relative", marginBottom: 20 }}>
                <form onSubmit={(e) => e.preventDefault()} autoComplete="off">
                  <Input
                    // type={showPassword ? "text" : 'password'}
                    type="password"
                    value={password}
                    onChange={(e) => handlePasswordChange(e)}
                    // className={classes.input}
                    name="password"
                    variant={"secondary"}
                    placeholder="Password"
                    containerStyle={{ marginBottom: 0 }}
                  />
                </form>
              </div>
            </>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
            {isShowCancel && (
              <MuiButton
                variant="text"
                onClick={() => handleClose()}
                className={classes.noUppercase}
              >
                Go Back
              </MuiButton>
            )}
            {!isEmailGrantLock && (
              <Box>
                <Button
                  isLoading={loading}
                  onClick={() => onButtonPress()}
                  sx={{ mt: 10 }}
                >
                  Confirm
                </Button>
              </Box>
            )}
          </Box>
        </div>
      </Fade>
    </Modal>
  );
};

export default AccessKeyEnterModal;
